import { createFileRoute, Outlet, redirect, useLocation } from '@tanstack/react-router';
import { BrandWorkbench } from '../components/brand-workbench/index.js';
import { FeedCardDrawer } from '../components/feed-card-drawer.js';
import { MagLauncher } from '../components/mag-launcher/mag-launcher.js';
import { setActiveBrandId } from '../lib/active-brand.js';
import { billingQuery, brandsQuery, meQuery } from '../lib/queries.js';

// D1 Phase 3+ — `/brand/$slug` is the layout for every brand-scoped
// route. Resolves the slug from the URL against the org's brands,
// pins it as the active brand (so non-restructured routes + the
// switcher chip stay in sync), and renders the matched child.
//
// Bare `/brand/$slug` (no sub-path) redirects to the per-brand /home.
// Children are at `/brand/$slug/home`, `/brand/$slug/brief`, etc.

export const Route = createFileRoute('/brand/$slug')({
  beforeLoad: async ({ params, context: { queryClient }, location }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    const brands = await queryClient.ensureQueryData(brandsQuery());
    const match = brands.find((b) => b.slug === params.slug);
    // Unknown slug → drop them on the manage page so they can see
    // what they actually have access to. Never silently land them on
    // someone else's brand.
    if (!match) throw redirect({ to: '/brands' });
    // Side-effect: keep localStorage in sync so any not-yet-migrated
    // flat route that still uses useActiveBrandId() sees the same
    // brand the URL is showing. Will be removed when every brand-
    // scoped route has been moved under this layout.
    setActiveBrandId(match.id);
    // Value-first paywall gate. This layout's beforeLoad is inherited by
    // every brand-scoped route, so one check covers home, campaigns,
    // channels, analytics, settings, etc. EXCEPTION: the onboarding
    // preview journey stays open while unpaid — that's what earns the
    // payment, and the paywall lands at the END of it. The journey spans
    // two path families: the `/onboarding/*` steps (url → scrape →
    // assets → competition) AND the `/brief*` brand-brain review
    // (brief → voice → customer → confirm), which sit between scrape and
    // assets. Both must stay reachable; gating /brief would bounce the
    // founder to the paywall right after the scrape, before they ever
    // see the competition playbook. An unpaid founder who reaches any
    // OTHER brand surface is bounced to /onboarding/plan, carrying the
    // slug so checkout returns to this brand's home. (Frontend gate only
    // — matches the app's existing posture; not a hardened boundary.)
    const inOnboardingPreview =
      location.pathname.includes('/onboarding/') || location.pathname.includes('/brief');
    if (!inOnboardingPreview) {
      const billing = await queryClient.ensureQueryData(billingQuery());
      const paid =
        billing.subscriptionStatus === 'active' || billing.subscriptionStatus === 'trialing';
      if (!paid) {
        // Forward ?checkout=success when present: the founder just paid
        // and the subscription webhook is still in flight (it lags the
        // browser redirect). Carrying the flag lets the plan page show
        // its "finalizing" poll instead of re-showing the picker to
        // someone who already paid.
        const searchRecord = location.search as Record<string, unknown>;
        const finalizing = searchRecord['checkout'] === 'success';
        throw redirect({
          to: '/onboarding/plan',
          search: { brand: params.slug, ...(finalizing ? { checkout: 'success' } : {}) },
        });
      }
    }
    // Bare `/brand/$slug` → bounce to per-brand home so the founder
    // never lands on a blank Outlet.
    if (location.pathname === `/brand/${params.slug}`) {
      throw redirect({ to: '/brand/$slug/home', params: { slug: params.slug } });
    }
    return { brand: match };
  },
  component: BrandLayout,
});

// Workbench drawer + MagLauncher mount alongside the outlet so they're
// available across every brand-scoped sub-route. Both gate on
// brand.onboardedAt internally; we additionally pass `visible=false`
// on routes where even a settled brand shouldn't see them (OAuth
// pickers, mid-onboarding flows). The workbench's manual trigger now
// lives in the home sidebar (SidebarBrandCard) — the drawer still
// mounts here so deep-links + feed-card CTAs open it everywhere — and
// MagLauncher owns the bottom-right floating slot for Mag chat.
//
// FeedCardDrawer also mounts here (s216-54h.35). Used to live only
// inside /home, which meant notification deep-links landed the founder
// on /home before the drawer could open — and any non-home brand-
// scoped page that linked to a card with ?card=<id> got ignored. By
// hoisting it to the layout, the drawer follows the founder across
// /campaigns, /channels, /analytics, etc. — anywhere they can click
// a notification or share a deep-link.
function BrandLayout() {
  const { brand } = Route.useRouteContext();
  const location = useLocation();
  const path = location.pathname;
  const hideWorkbench =
    path.includes('/meta-connect/') ||
    path.includes('/google-connect/') ||
    path.includes('/onboarding/');
  // [2026-10-03] On a phone the 56px floating bubble lands exactly on
  // the composer's send button, on STOP DRAFTING, and on the keyword
  // chips — founder-reported with screenshots. On surfaces that
  // already put mag on screen there is nothing to lose by hiding it:
  // she is right there. Desktop keeps it; there is room.
  const hasInlineMagChat = path.endsWith('/home') || path.includes('/campaigns/new/brief');
  // Parse ?card=<id> off the live URL. We can't use the home route's
  // typed search here — this layout sits above every brand-scoped
  // route, most of which don't declare card in their validateSearch.
  // TanStack hands us `location.search` as a parsed object whose
  // shape depends on the matched route; cast loose + sniff the key.
  const searchRecord = location.search as Record<string, unknown>;
  const rawCard = searchRecord['card'];
  const cardId = typeof rawCard === 'string' && rawCard.length > 0 ? rawCard : null;
  return (
    <>
      <Outlet />
      <BrandWorkbench brand={brand} visible={!hideWorkbench} />
      <MagLauncher brand={brand} visible={!hideWorkbench} hideOnMobile={hasInlineMagChat} />
      {cardId ? <FeedCardDrawer brandId={brand.id} brandSlug={brand.slug} cardId={cardId} /> : null}
    </>
  );
}
